import React from 'react';
import FileCard from './FileCard';
import { FaSpinner, FaExclamationTriangle, FaFolder, FaHome, FaChevronRight, FaArrowLeft, FaFile } from 'react-icons/fa';
import { isFolder } from '../utils/fileUtils';

const FileGrid = ({ 
  files, 
  loading, 
  error, 
  selectedFiles, 
  onFileSelect, 
  onFileDownload, 
  onFileDelete, 
  onFilePreview, 
  onFileRename,
  onFileTags,
  onFolderTags,
  currentPath,
  navigationHistory,
  onFolderOpen,
  onNavigateBack,
  onNavigateToPath,
  rootFoldersLoading,
  rootFoldersLoaded,
  isSearching = false,
  searchTerm = '',
  selectedTag = null,
  tagResultsLoading = false,
  tagResultsError = null,
  selectedFolderTag = null,
  folderTagResultsLoading = false,
  folderTagResultsError = null,
  onShareFile
}) => {
  
  // En la raíz, mostrar loading si las carpetas raíz están cargando
  if (currentPath === '' && rootFoldersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <FaSpinner className="text-4xl text-blue-500 animate-spin" />
          <p className="text-gray-400">Cargando carpetas...</p>
        </div>
      </div>
    );
  }
  
  if (tagResultsLoading || folderTagResultsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <FaSpinner className="text-4xl text-blue-500 animate-spin" />
          <p className="text-gray-400">
            {folderTagResultsLoading ? 'Cargando carpetas del tag seleccionado...' : 'Cargando archivos del tag seleccionado...'}
          </p>
        </div>
      </div>
    );
  }

  if (tagResultsError || folderTagResultsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4 text-center">
          <FaExclamationTriangle className="text-4xl text-red-500" />
          <p className="text-red-400">Error al cargar {folderTagResultsError ? 'carpetas' : 'archivos'} del tag</p>
          <p className="text-gray-500 text-sm">{folderTagResultsError || tagResultsError}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <FaSpinner className="text-4xl text-blue-500 animate-spin" />
          <p className="text-gray-400">Cargando archivos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4 text-center">
          <FaExclamationTriangle className="text-4xl text-red-500" />
          <p className="text-red-400">Error al cargar archivos</p>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!files || files.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4 text-center">
          <FaFolder className="text-4xl text-gray-500" />
          <p className="text-gray-400">
            {selectedFolderTag ? `No hay carpetas con el tag #${selectedFolderTag}` : 
             selectedTag ? `No hay archivos con el tag #${selectedTag}` : 
             'No hay archivos para mostrar'}
          </p>
          {!selectedTag && !selectedFolderTag && (
            <p className="text-gray-500 text-sm">Sube algunos archivos para comenzar</p>
          )}
        </div>
      </div>
    );
  }

  // Separar carpetas y archivos
  const folders = files.filter(file => isFolder(file));
  const regularFiles = files.filter(file => !isFolder(file));

  return (
    <div className="p-6">
      {selectedFolderTag && selectedTag ? (
        <div className="mb-6 space-y-2">
          <h2 className="text-xl font-semibold text-yellow-200">
            Carpetas etiquetadas con #{selectedFolderTag}
          </h2>
          <p className="text-sm text-gray-400">
            {folders.length.toLocaleString()} carpeta(s) y {regularFiles.length.toLocaleString()} archivo(s) con tag #{selectedTag}
          </p>
        </div>
      ) : selectedFolderTag ? (
        <div className="mb-6 space-y-2">
          <h2 className="text-xl font-semibold text-yellow-200">
            Carpetas etiquetadas con #{selectedFolderTag}
          </h2>
          <p className="text-sm text-gray-400">
            {folders.length.toLocaleString()} resultado(s)
          </p>
        </div>
      ) : selectedTag ? (
        <div className="mb-6 space-y-2">
          <h2 className="text-xl font-semibold text-blue-200">
            Archivos etiquetados con #{selectedTag}
          </h2>
          <p className="text-sm text-gray-400">
            {files.length.toLocaleString()} resultado(s)
          </p>
        </div>
      ) : !isSearching && (
        <div className="mb-6 space-y-4">
          <div className="flex items-center space-x-2">
            {/* Botón de retroceso */}
            {currentPath && navigationHistory.length > 1 && (
              <button
                onClick={onNavigateBack}
                className="flex items-center space-x-1 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300 hover:text-white transition-colors"
              >
                <FaArrowLeft className="text-sm" />
                <span>Atrás</span>
              </button>
            )}
            
            {/* Breadcrumb */}
            <div className="flex items-center space-x-1 text-sm">
              <button
                onClick={() => onNavigateToPath('')}
                className={`flex items-center space-x-1 px-2 py-1 rounded transition-colors ${
                  currentPath === '' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <FaHome />
                <span>Inicio</span>
              </button>
              
              {currentPath && currentPath.split('/').filter(part => part !== '').map((part, index, array) => {
                const isLast = index === array.length - 1;
                const pathToHere = array.slice(0, index + 1).join('/') + '/';
                
                return (
                  <React.Fragment key={index}>
                    <FaChevronRight className="text-gray-500 text-xs" />
                    <button
                      onClick={() => onNavigateToPath(pathToHere)}
                      className={`px-2 py-1 rounded transition-colors ${
                        isLast ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                    >
                      {part}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Sección de Carpetas */}
      {folders.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <FaFolder className="text-slate-400" />
            <h2 className="text-lg font-semibold text-gray-400">
              Carpetas sugeridas ({folders.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {folders.map((file, index) => {
              const fileName = file.Key || file.name || `folder-${index}`;
              const uniqueKey = file.isVirtualFolder ? `virtual-${fileName}` : `real-${fileName}`;
              const isSelected = selectedFiles.includes(fileName);
              
              return (
                <FileCard
                  key={uniqueKey}
                  file={file}
                  isSelected={isSelected}
                  onSelect={onFileSelect}
                  onDownload={onFileDownload}
                  onDelete={onFileDelete}
                  onPreview={onFilePreview}
                  onRename={onFileRename}
                  onEditTags={onFileTags}
                  onEditFolderTags={onFolderTags}
                  onFolderOpen={onFolderOpen}
                  onShareFile={onShareFile}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Sección de Archivos */}
      {regularFiles.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <FaFile className="text-slate-400"/>
            <h2 className="text-lg font-semibold text-gray-400">
              Archivos sugeridos ({regularFiles.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {regularFiles.map((file, index) => {
              const fileName = file.Key || file.name || `file-${index}`;
              const uniqueKey = `file-${fileName}-${index}`;
              const isSelected = selectedFiles.includes(fileName);
              
              return (
                <FileCard
                  key={uniqueKey}
                  file={file}
                  isSelected={isSelected}
                  onSelect={onFileSelect}
                  onDownload={onFileDownload}
                  onDelete={onFileDelete}
                  onPreview={onFilePreview}
                  onRename={onFileRename}
                  onEditTags={onFileTags}
                  onEditFolderTags={onFolderTags}
                  onFolderOpen={onFolderOpen}
                  onShareFile={onShareFile}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Mensaje cuando no hay carpetas ni archivos específicos */}
      {folders.length === 0 && regularFiles.length === 0 && (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center space-y-4 text-center">
            <FaFolder className="text-4xl text-gray-500" />
            <p className="text-gray-400">No hay elementos para mostrar</p>
            <p className="text-gray-500 text-sm">Crea carpetas o sube archivos para comenzar</p>
          </div>
        </div>
      )}

      {/* Show count at bottom */}
      <div className="mt-8 text-center">
        <p className="text-gray-500 text-sm">
          Mostrando {folders.length} carpeta{folders.length !== 1 ? 's' : ''} y {regularFiles.length} archivo{regularFiles.length !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

export default FileGrid; 